/*
  Shift modular sections · chapter video (<sr-chapter>)
  - The chapter list follows the visible video's playhead: earlier chapters show
    as done, the live chapter shows its progress and current step.
  - Clicking a chapter jumps the video there and plays it.
  - Desktop: play button over the poster; tapping the playing video pauses it.
    Pauses when scrolled out of view.
  - Mobile autoplay / pause button come from sr-media.js (never autoplays with
    reduced motion).
  - Sound buttons (desktop and/or mobile) switch their own video's sound.
    Desktop starts muted unless data-sound="true"; the button works before play.
    Mobile autoplay always starts muted; turning sound on also starts the video
    if it isn't playing and the visitor didn't pause it.
  - Optional captions: WebVTT text from the section, drawn over whichever
    video is playing (above the pill on desktop, above the buttons on mobile).
  Times are m:ss (decimals allowed), e.g. 0:38 or 1:26.4.
*/
(function () {
  if (customElements.get('sr-chapter')) return;

  function parseTime(value) {
    if (value == null) return NaN;
    var str = String(value).trim();
    if (!str) return NaN;
    var parts = str.split(':');
    var total = 0;
    for (var i = 0; i < parts.length; i++) {
      var n = parseFloat(parts[i]);
      if (isNaN(n)) return NaN;
      total = total * 60 + n;
    }
    return total;
  }

  // Cues from WebVTT text: [{ start, end, lines }]. NOTE blocks and cue settings are ignored.
  function parseVtt(text) {
    var stamp = function (t) {
      var p = t.trim().split(':');
      var total = 0;
      for (var i = 0; i < p.length; i++) total = total * 60 + parseFloat(p[i]);
      return total;
    };
    return String(text).replace(/\r/g, '').split(/\n{2,}/).map(function (block) {
      var lines = block.split('\n');
      var at = -1;
      for (var i = 0; i < lines.length; i++) {
        if (lines[i].indexOf('-->') !== -1) { at = i; break; }
      }
      if (at === -1) return null;
      var times = lines[at].split('-->');
      return { start: stamp(times[0]), end: stamp(times[1].trim().split(/\s+/)[0]), lines: lines.slice(at + 1).filter(Boolean) };
    }).filter(function (cue) { return cue && cue.lines.length && !isNaN(cue.start) && !isNaN(cue.end); });
  }

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class SRChapter extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('[data-sr-chapter]')).map(function (li) {
        return {
          el: li,
          btn: li.querySelector('.sr-chapter__btn'),
          fill: li.querySelector('[data-sr-fill]'),
          start: parseTime(li.dataset.start) || 0,
          steps: Array.from(li.querySelectorAll('[data-sr-step]'))
            .map(function (s) { return { el: s, start: parseTime(s.dataset.start) }; })
            .filter(function (s) { return !isNaN(s.start); })
        };
      });
      this.endTime = parseTime(this.dataset.end);
      this.desktop = this.querySelector('[data-sr-chapter-media="desktop"]');
      this.desktopVideo = this.desktop ? this.desktop.querySelector('video') : null;
      this.playBtn = this.querySelector('[data-sr-chapter-play]');
      this.pauseBtn = this.querySelector('[data-sr-chapter-pause]');
      this.videos = Array.from(this.querySelectorAll('[data-sr-chapter-media] video'));
      var vtt = this.querySelector('[data-sr-chapter-vtt]');
      this.cues = vtt ? parseVtt(vtt.textContent) : [];
      // Desktop "start with sound": unmuted before play (a click starts it, so browsers allow it)
      if (this.desktopVideo && this.dataset.sound === 'true') this.desktopVideo.muted = false;

      this.onTime = this.onTime.bind(this);
      this.onPlay = this.onPlay.bind(this);
      this.onPause = this.onPause.bind(this);
      this.tick = this.tick.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);

      var self = this;
      this.videos.forEach(function (v) {
        v.addEventListener('timeupdate', self.onTime);
        v.addEventListener('seeked', self.onTime);
        v.addEventListener('play', self.onPlay);
        v.addEventListener('pause', self.onPause);
      });

      this.items.forEach(function (item) {
        if (item.btn) item.btn.addEventListener('click', function () { self.jump(item.start); });
      });

      if (this.playBtn) this.playBtn.addEventListener('click', function () { self.play(self.desktopVideo); });
      if (this.pauseBtn) {
        this.pauseBtn.addEventListener('click', function () {
          if (self.desktopVideo) self.desktopVideo.pause();
        });
      }

      this.querySelectorAll('[data-sr-chapter-sound]').forEach(function (btn) {
        var media = btn.closest('[data-sr-chapter-media]');
        var video = media ? media.querySelector('video') : null;
        if (!video) return;
        btn.addEventListener('click', function () { self.toggleSound(video); });
        video.addEventListener('volumechange', function () { self.syncSound(btn, video); });
        self.syncSound(btn, video);
      });

      // A desktop video someone started stops when it leaves the screen
      if (this.desktopVideo && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting && !self.desktopVideo.paused) self.desktopVideo.pause();
          });
        });
        this.observer.observe(this.desktop);
      }

      document.addEventListener('shopify:block:select', this.onBlockSelect);
      this.render(0);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      window.cancelAnimationFrame(this.frame);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    activeVideo() {
      for (var i = 0; i < this.videos.length; i++) {
        if (this.videos[i].offsetParent !== null) return this.videos[i];
      }
      return null;
    }

    end(video) {
      if (!isNaN(this.endTime)) return this.endTime;
      if (video && isFinite(video.duration) && video.duration > 0) return video.duration;
      return Infinity;
    }

    seek(video, time) {
      if (video.readyState >= 1) {
        video.currentTime = time;
      } else {
        video.addEventListener('loadedmetadata', function () { video.currentTime = time; }, { once: true });
        if (video.preload === 'none') video.preload = 'metadata';
      }
    }

    play(video) {
      if (!video) return;
      var slot = video.closest('[data-sr-video]');
      if (slot) slot.removeAttribute('data-user-paused');
      if (slot && window.SRMedia) {
        window.SRMedia.play(slot);
      } else {
        var p = video.play();
        if (p && p.catch) p.catch(function () {});
      }
    }

    // A tap is the gesture browsers need before they allow sound
    toggleSound(video) {
      video.muted = !video.muted;
      if (video === this.desktopVideo) return; // desktop waits for its play button
      var slot = video.closest('[data-sr-video]');
      if (!video.muted && video.paused && !(slot && slot.hasAttribute('data-user-paused'))) this.play(video);
    }

    syncSound(btn, video) {
      btn.setAttribute('aria-pressed', video.muted ? 'false' : 'true');
    }

    jump(time) {
      var video = this.activeVideo();
      this.render(time);
      if (!video) return;
      this.seek(video, time);
      this.play(video);
    }

    onTime(event) {
      if (event.target === this.activeVideo()) this.render(event.target.currentTime);
      this.caption(event.target);
    }

    // Shows the cue at the video's time in its own caption box; nothing before it has started
    caption(video) {
      if (!this.cues.length || !video) return;
      var media = video.closest('[data-sr-chapter-media]');
      var box = media ? media.querySelector('[data-sr-chapter-captions]') : null;
      if (!box) return;
      var time = video.currentTime;
      var index = -1;
      if (time > 0) {
        for (var i = 0; i < this.cues.length; i++) {
          if (time >= this.cues[i].start && time < this.cues[i].end) { index = i; break; }
        }
      }
      if (String(index) === box.dataset.cue) return;
      box.dataset.cue = String(index);
      box.textContent = '';
      if (index === -1) { box.hidden = true; return; }
      this.cues[index].lines.forEach(function (line) {
        var span = document.createElement('span');
        span.textContent = line;
        box.appendChild(span);
      });
      box.hidden = false;
    }

    onPlay(event) {
      var video = event.target;
      if (video === this.desktopVideo) {
        var hadFocus = document.activeElement === this.playBtn;
        this.desktop.setAttribute('data-playing', '');
        if (hadFocus && this.pauseBtn) this.pauseBtn.focus();
      }
      if (!reduceMotion.matches) {
        window.cancelAnimationFrame(this.frame);
        this.frame = window.requestAnimationFrame(this.tick);
      }
    }

    onPause(event) {
      var video = event.target;
      if (video === this.desktopVideo) {
        var hadFocus = document.activeElement === this.pauseBtn;
        this.desktop.removeAttribute('data-playing');
        if (hadFocus && this.playBtn) this.playBtn.focus();
      }
    }

    // Smooth progress while playing (timeupdate only fires about 4 times a second)
    tick() {
      var video = this.activeVideo();
      if (!video || video.paused) return;
      this.render(video.currentTime);
      this.caption(video);
      this.frame = window.requestAnimationFrame(this.tick);
    }

    onBlockSelect(event) {
      if (!this.contains(event.target)) return;
      var time = parseTime(event.target.dataset && event.target.dataset.start);
      if (!isNaN(time)) this.render(time);
    }

    render(time) {
      var items = this.items;
      if (!items.length) return;
      var end = this.end(this.activeVideo());
      var allDone = time >= end;
      var active = 0;
      for (var i = 0; i < items.length; i++) {
        if (time >= items[i].start) active = i;
      }

      for (var j = 0; j < items.length; j++) {
        var item = items[j];
        var isActive = !allDone && j === active;
        var isDone = allDone || j < active;
        item.el.classList.toggle('is-active', isActive);
        item.el.classList.toggle('is-done', isDone);
        if (item.btn) {
          if (isActive) item.btn.setAttribute('aria-current', 'step');
          else item.btn.removeAttribute('aria-current');
        }

        var progress = 0;
        if (isActive) {
          var next = j + 1 < items.length ? items[j + 1].start : end;
          if (isFinite(next) && next > item.start) {
            progress = Math.min(Math.max((time - item.start) / (next - item.start), 0), 1);
          }
        }
        if (item.fill) item.fill.style.setProperty('--sr-progress', progress.toFixed(4));

        var current = -1;
        if (isActive) {
          for (var k = 0; k < item.steps.length; k++) {
            if (time >= item.steps[k].start) current = k;
          }
        }
        item.steps.forEach(function (step, index) {
          step.el.classList.toggle('is-current', index === current);
        });
      }
    }
  }

  customElements.define('sr-chapter', SRChapter);
})();
